<div class="lq">
  <p class="wd-note" *ngIf="loading">Loading…</p>
  <div class="br-alert br-alert--danger lq-error" *ngIf="loadError"><br-icon>error_outline</br-icon> {{ loadError }}</div>

  <!-- Raising one. Above the list rather than below it: on a lead with three
       revisions the button to add a fourth would otherwise be past all of
       them. -->
  <section class="wd-section lq-compose" *ngIf="composing">
    <h3 class="br-section-title br-mb-3">New quote</h3>

    <!-- What is being quoted, before what it costs. A quote is the document
         somebody is asked to agree to, so it has to say what the arrangement
         IS - the same things the contract accepting it will state, minus the
         signature, which is the whole difference between the two. -->
    <mat-form-field appearance="outline" subscriptSizing="dynamic" class="lq-wide">
      <mat-label>Title</mat-label>
      <input matInput [(ngModel)]="draftTerms.title"
             placeholder="Weekly commercial collection" />
    </mat-form-field>

    <div class="lq-row">
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Service from</mat-label>
        <input matInput type="date" [(ngModel)]="draftTerms.effectiveFrom" />
      </mat-form-field>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Service to</mat-label>
        <input matInput type="date" [(ngModel)]="draftTerms.effectiveTo" />
      </mat-form-field>
    </div>

    <div class="lq-row">
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Billing method</mat-label>
        <mat-select [(ngModel)]="draftTerms.billingMethod">
          <mat-option [value]="''">Not stated</mat-option>
          <mat-option *ngFor="let m of billingMethods" [value]="m.value">{{ m.label }}</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Billing day</mat-label>
        <input matInput type="number" min="1" max="28"
               [(ngModel)]="draftTerms.billingDayOfMonth" />
      </mat-form-field>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Services per month</mat-label>
        <input matInput type="number" min="0"
               [(ngModel)]="draftTerms.expectedMonthlyCount" />
      </mat-form-field>
    </div>

    <div class="lq-row">
      <!-- Chosen here so Accept does not ask again: the code quoted under is
           the code the contract is billed under. -->
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Billing code</mat-label>
        <mat-select [(ngModel)]="draftTerms.transactionCodeId">
          <mat-option [value]="null">Not stated</mat-option>
          <mat-option *ngFor="let code of transactionCodes" [value]="code.id">
            {{ code.shortDescription }}</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>PO / release number</mat-label>
        <input matInput [(ngModel)]="draftTerms.poReleaseNumber" />
      </mat-form-field>
    </div>

    <mat-form-field appearance="outline" subscriptSizing="dynamic" class="lq-wide">
      <mat-label>Terms</mat-label>
      <textarea matInput rows="3" [(ngModel)]="draftTerms.terms"
                placeholder="What the customer is agreeing to"></textarea>
    </mat-form-field>

    <div class="lq-line" *ngFor="let line of draftLines; let i = index">
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="lq-line__what">
        <mat-label>What is being quoted</mat-label>
        <input matInput [(ngModel)]="line.description" placeholder="Weekly 4-yard pickup" />
      </mat-form-field>
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="lq-line__amount">
        <mat-label>Per month</mat-label>
        <input matInput [(ngModel)]="line.amount" inputmode="decimal" placeholder="0.00" />
      </mat-form-field>
      <button mat-icon-button type="button" (click)="removeLine(i)"
              [disabled]="draftLines.length === 1"
              matTooltip="Remove this line" aria-label="Remove this line">
        <br-icon>close</br-icon>
      </button>
    </div>

    <div class="lq-compose__foot">
      <button class="br-btn br-btn--link br-btn--sm" type="button" (click)="addLine()">
        + Add a line
      </button>
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="lq-until">
        <mat-label>Valid until (optional)</mat-label>
        <input matInput type="date" [(ngModel)]="draftValidUntil" />
      </mat-form-field>
      <span class="lq-total">
        {{ draftTotal | currency:'USD':'symbol':'1.2-2' }}<span class="lq-per">/mo</span>
      </span>
    </div>
    <!-- Said out loud: a blank row somebody tabbed past is not a free line,
         and a quote of nothing is not a quote. -->
    <p class="wd-note" *ngIf="!draftValid">
      Give at least one line a monthly amount. Rows left blank are not included.
    </p>
    <div class="br-flex br-items-center br-flex-wrap br-gap-2 br-mt-3">
      <button type="button" class="br-btn br-btn--primary br-btn--sm"
              [disabled]="!draftValid || creating" (click)="createQuote()">
        {{ creating ? 'Saving…' : 'Save the quote' }}
      </button>
      <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
              (click)="cancelCompose()">Cancel</button>
    </div>
  </section>

  <!-- The quotes themselves, newest revision first. -->
  <section class="wd-section" *ngIf="!composing">
    <div class="br-flex br-items-center br-justify-between br-flex-wrap br-gap-2 br-mb-3">
      <h3 class="br-section-title br-mb-0">Quotes <span class="wd-count">{{ quotes.length }}</span></h3>
      <button type="button" class="br-btn br-btn--primary br-btn--sm"
              *ngIf="canManage" (click)="startCompose()">
        <br-icon>request_quote</br-icon> Raise a quote
      </button>
    </div>
    <p class="wd-note" *ngIf="!canManage">Read-only for your role.</p>

    <div class="lq-quote" *ngFor="let quote of quotes"
         [class.lq-quote--done]="isTerminal(quote)">
      <div class="lq-quote__head">
        <span class="lq-quote__name">v{{ quote.version }}</span>
        <span class="br-badge br-badge--neutral lq-status" [attr.data-status]="quote.status">
          {{ statusLabel(quote.status) }}</span>
        <span class="lq-quote__money" *ngIf="quote.monthlyTotal != null">
          {{ quote.monthlyTotal | currency:'USD':'symbol':'1.2-2' }}<span class="lq-per">/mo</span>
        </span>
      </div>

      <!-- What this one is quoting. Read back where it was written, or the
           fields would be a form nobody could check afterwards. -->
      <div class="lq-quote__title" *ngIf="quote.title">{{ quote.title }}</div>
      <dl class="lq-terms" *ngIf="quote.effectiveFrom || quote.billingMethod
                                  || quote.expectedMonthlyCount != null
                                  || quote.poReleaseNumber">
        <ng-container *ngIf="quote.effectiveFrom">
          <dt>Service</dt>
          <dd>{{ quote.effectiveFrom }}<span *ngIf="quote.effectiveTo"> to {{ quote.effectiveTo }}</span></dd>
        </ng-container>
        <ng-container *ngIf="quote.billingMethod">
          <dt>Billing</dt>
          <dd>{{ billingLabel(quote.billingMethod) }}<span
                *ngIf="quote.billingDayOfMonth != null">, day {{ quote.billingDayOfMonth }}</span></dd>
        </ng-container>
        <ng-container *ngIf="quote.expectedMonthlyCount != null">
          <dt>Per month</dt>
          <dd>{{ quote.expectedMonthlyCount }} services</dd>
        </ng-container>
        <ng-container *ngIf="quote.poReleaseNumber">
          <dt>PO</dt>
          <dd>{{ quote.poReleaseNumber }}</dd>
        </ng-container>
      </dl>
      <p class="lq-quote__terms" *ngIf="quote.terms">{{ quote.terms }}</p>

      <div class="lq-quote__meta">
        <span *ngIf="quote.validUntil">valid until {{ quote.validUntil }}</span>
        <span *ngIf="quote.acceptedAt">accepted {{ quote.acceptedAt | date:'MMM d, y' }}</span>
        <!-- The whole point of the tab: accepting is what MAKES the contract,
             so the contract is named where it came from. -->
        <span class="lq-contract" *ngIf="quote.acceptedContractId != null">
          <br-icon class="lq-mini-icon">description</br-icon>
          contract #{{ quote.acceptedContractId }}
        </span>
      </div>

      <ul class="lq-lines" *ngIf="quote.lines.length">
        <li *ngFor="let line of quote.lines">
          <span class="lq-lines__what">{{ line.description || line.lineKey }}</span>
          <span class="lq-lines__amount">
            {{ line.monthlyAmount | currency:'USD':'symbol':'1.2-2' }}</span>
        </li>
      </ul>

      <!-- Accepting: a contract needs a billing code and a term, so it asks
           for both rather than inventing them. -->
      <div class="lq-accept" *ngIf="acceptingId === quote.id">
        <p class="wd-note">
          Accepting writes the contract. If this lead has no customer account yet, one is
          created with it.
        </p>
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>Billed as</mat-label>
          <mat-select [(ngModel)]="acceptDraft.transactionCodeId">
            <mat-option *ngFor="let code of transactionCodes" [value]="code.id">
              {{ code.shortDescription }}</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>Effective from</mat-label>
          <input matInput type="date" [(ngModel)]="acceptDraft.effectiveFrom" />
        </mat-form-field>
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>Until</mat-label>
          <input matInput type="date" [(ngModel)]="acceptDraft.effectiveTo" />
        </mat-form-field>
        <div class="lq-accept__actions">
          <button class="br-btn br-btn--primary br-btn--sm"
                  [disabled]="!acceptValid || busyId === quote.id"
                  (click)="confirmAccept(quote)">
            {{ busyId === quote.id ? 'Accepting…' : 'Accept and write the contract' }}
          </button>
          <button class="br-btn br-btn--outline-secondary br-btn--sm"
                  (click)="cancelAccept()">Cancel</button>
        </div>
      </div>

      <div class="lq-quote__actions" *ngIf="acceptingId !== quote.id">
        <!-- The internal yes, under its own permission and only where it can
             land. Refusing is offered beside it: a review with only one answer
             is not a review. -->
        <ng-container *ngIf="canApproveNow(quote)">
          <button class="br-btn br-btn--primary br-btn--sm"
                  [disabled]="busyId === quote.id" (click)="approve(quote, true)">
            Approve
          </button>
          <button class="br-btn br-btn--outline-secondary br-btn--sm"
                  [disabled]="busyId === quote.id" (click)="approve(quote, false)">
            Send back
          </button>
        </ng-container>

        <button class="br-btn br-btn--primary br-btn--sm" *ngIf="canAcceptNow(quote)"
                [disabled]="busyId === quote.id" (click)="startAccept(quote)">
          <br-icon>task_alt</br-icon> Customer accepted
        </button>

        <button class="br-btn br-btn--outline-secondary br-btn--sm"
                *ngFor="let to of movesFrom(quote)"
                [disabled]="busyId === quote.id" (click)="move(quote, to)">
          {{ statusLabel(to) }}
        </button>

        <span class="wd-note" *ngIf="isTerminal(quote)">
          This quote is closed — raise a new one to change the offer.
        </span>
      </div>
    </div>

    <p class="wd-note" *ngIf="!loading && !quotes.length">
      No quote has been raised for this lead. A quote is the lead's paperwork: accepting one
      writes the contract, and creates the customer account if there is not one yet.
    </p>
  </section>

  <div class="br-alert br-alert--danger lq-error" *ngIf="commandError"><br-icon>block</br-icon> {{ commandError }}</div>
</div>
